import React, { FC } from 'react'
import { Tooltip } from 'transfix-ui/components'
import { makeStyles, Typography } from 'transfix-ui/core'
import { IShipperRateTypes } from 'types/graphqlTypes'
import { useIssuePriorityLabel } from 'apps/marketplace/pages/issues/shared/priorityOptions'
import EquipmentIcon from 'apps/marketplace/pages/shipment-details/components/EquipmentIcon'
import ShipmentIcons, {
  getIcons,
} from 'apps/marketplace/pages/shipment-details/components/ShipmentIcons'
import { companyNameInitials } from 'utils/initials'
import { DISPLAY } from 'constants/display'
import { IEscalatedIssueFragment } from '../../graphql/EscalatedIssueFragment'

interface IIssueRowProps {
  issue: IEscalatedIssueFragment
}

const useIssueRowStyles = makeStyles(() => ({
  equipment: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  icons: {
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flexWrap: 'wrap',
    },
  },
  priority: {
    display: 'flex',
    alignItems: 'center',
  },
  shipperName: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
}))

export const IssueNotificationRow: FC<IIssueRowProps> = React.memo(({ issue }) => {
  const classes = useIssueRowStyles()
  const { id, priority, shipment, type } = issue
  const { category, name: typeName } = type || {}
  const { name: typeCategoryName } = category || {}
  const { characteristics, equipment, id: shipmentId, shipperApplication, shipperRateType } =
    shipment || {}
  const { name: shipperName } = shipperApplication || {}

  const icons = getIcons({
    ...characteristics,
    pricedUnderContract: shipperRateType === IShipperRateTypes.contract_primary,
    contractBackup: shipperRateType === IShipperRateTypes.contract_backup,
    sameDaySpot: shipperRateType === IShipperRateTypes.spot,
  })

  const { className: priorityClassName, short: priorityLabel } = useIssuePriorityLabel(priority)

  return (
    <>
      <div>
        <Typography variant='subtitle2'>{typeCategoryName}</Typography>
      </div>
      <div>
        <a
          href={`${__ROOT__}/shipment-details/${shipmentId}?issueId=${id}`}
          target='_blank'
          rel='noopener noreferrer'
          className={classes.shipperName}>
          <Typography variant='button'>{typeName}</Typography>
        </a>
      </div>
      <div className={classes.priority}>
        <Typography variant='body1' className={priorityClassName}>
          {priorityLabel || DISPLAY.empty}
        </Typography>
      </div>
      <div className={classes.equipment}>
        <EquipmentIcon
          tooltip={equipment.name}
          icon={equipment.parentKey}
          className={classes.shipperName}
        />
        <Tooltip
          trigger={
            <Typography component='span' variant='body1' className={classes.shipperName}>
              {companyNameInitials(shipperName)}
            </Typography>
          }>
          {shipperName}
        </Tooltip>
        <a
          href={`${__ROOT__}/shipment-details/${shipmentId}`}
          target='_blank'
          rel='noopener noreferrer'
          className={classes.shipperName}>
          <Typography variant='body1'>#{shipmentId}</Typography>
        </a>
      </div>
      <div className={classes.icons}>
        <ShipmentIcons
          icons={icons}
          bouncedCount={characteristics?.bounceCount}
          rolledCount={characteristics?.rolloverCount}
        />
      </div>
    </>
  )
})
